import { create } from 'zustand';
import type { AuthResponse, LoginInput, RegisterUserInput, User } from '@orbit-sync/shared-types';
import { api, isApiRequestError, onSessionRefreshed, refreshSession } from '../lib/api/index.js';
import {
  clearAccessToken,
  getAccessToken,
  setAccessToken,
  subscribeToAccessToken,
} from '../lib/tokenStorage.js';

/**
 * Authentication state.
 *
 * WHY ZUSTAND RATHER THAN CONTEXT: the token has to be readable from outside the
 * React tree - the fetch wrapper attaches it to every request, and the Socket.IO
 * factory needs it at handshake time. Context cannot be read outside a component,
 * so the Context version of this needs a module-level mirror of the same state,
 * which is a hand-rolled store with extra steps. Zustand also avoids re-rendering
 * every consumer when an unrelated slice changes, which matters once channel and
 * message state land next to this.
 *
 * The token itself deliberately does NOT live in this store - `lib/tokenStorage.ts`
 * owns it, so there is exactly one place where the storage policy is decided and
 * audited. This store holds the *session*: who is signed in, and whether we know yet.
 */

export type AuthStatus =
  /** Startup: a stored token is being validated. Render nothing decisive yet. */
  'loading' | 'authenticated' | 'unauthenticated';

export interface AuthState {
  status: AuthStatus;
  user: User | null;
  /** In-flight login/register, so forms can disable their submit button. */
  pending: boolean;
  /**
   * Why the session ended, when it was not the user's choice - shown once on
   * the sign-in page. `null` after an ordinary sign-out.
   */
  notice: string | null;

  /** Validates any stored token against `GET /auth/me`. Safe to call more than once. */
  bootstrap: () => Promise<void>;
  login: (input: LoginInput) => Promise<void>;
  register: (input: RegisterUserInput) => Promise<void>;
  /** Discards the session. `notice` explains an involuntary sign-out. */
  logout: (notice?: string) => void;
  /** Adopts a re-issued session - after an account change, say. */
  setSession: (session: AuthResponse) => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: 'loading',
  user: null,
  pending: false,
  notice: null,

  async bootstrap(): Promise<void> {
    const hadToken = getAccessToken() !== null;

    // The session cookie decides, not the stored token: it may be hours old,
    // or gone while the session itself is still alive.
    const outcome = await refreshSession();

    if (outcome.status === 'ok') {
      set({ status: 'authenticated', user: outcome.session.user });
      return;
    }

    if (outcome.status === 'failed' && hadToken) {
      // The server could not be asked (restarting, offline). The stored token
      // may still be good; let it prove itself.
      try {
        const user = await api.auth.me();
        set({ status: 'authenticated', user });
        return;
      } catch (error) {
        if (isApiRequestError(error) && error.status === 401) {
          clearAccessToken(null);
        }
      }
    }

    set({
      status: 'unauthenticated',
      user: null,
      // Ended while the app was closed: say so, once.
      ...(outcome.status === 'ended' && hadToken ? { notice: endedNotice } : {}),
    });
    endedNotice = null;
  },

  async login(input: LoginInput): Promise<void> {
    set({ pending: true });

    try {
      const session = await api.auth.login(input);
      setAccessToken(session.accessToken.token);
      set({ status: 'authenticated', user: session.user, pending: false, notice: null });
    } catch (error) {
      set({ pending: false });
      // Rethrown so the form can render field-level detail; the store only owns
      // session state, not how a particular screen presents a failure.
      throw error;
    }
  },

  async register(input: RegisterUserInput): Promise<void> {
    set({ pending: true });

    try {
      const session = await api.auth.register(input);
      setAccessToken(session.accessToken.token);
      set({ status: 'authenticated', user: session.user, pending: false, notice: null });
    } catch (error) {
      set({ pending: false });
      throw error;
    }
  },

  logout(notice?: string): void {
    // A deliberate sign-out ends the session on the server too, so the cookie
    // and token are useless afterwards. Sent before the token is dropped, so
    // it carries it; never awaited, so signing out is instant even offline.
    if (!notice && getAccessToken()) {
      api.auth.logout().catch(() => undefined);
    }

    // State first, token second: clearing the token notifies the subscriber
    // below, which must see a sign-out already under way and not mistake it
    // for an expiry.
    if (get().status !== 'unauthenticated' || notice) {
      set({ status: 'unauthenticated', user: null, notice: notice ?? null });
    }

    clearAccessToken(null);
  },

  setSession(session: AuthResponse): void {
    setAccessToken(session.accessToken.token);
    set({ user: session.user });
  },
}));

/** Shown on the sign-in page when a session ends without the user asking. */
export const SESSION_EXPIRED_NOTICE = 'Your session has expired. Please sign in again.';

/**
 * Keeps the store honest when the token is dropped from outside React - the API
 * client clearing an expired one after a 401. Without this the UI would keep
 * rendering a signed-in shell whose every request fails.
 */
/** The server's reason for an ending noticed during bootstrap, shown once. */
let endedNotice: string | null = null;

subscribeToAccessToken((token, notice) => {
  if (token !== null) {
    return;
  }

  const state = useAuthStore.getState();

  if (state.status === 'authenticated') {
    useAuthStore.setState({
      status: 'unauthenticated',
      user: null,
      // `null`: a deliberate sign-out, here or in another tab - nothing to explain.
      notice: notice === null ? null : (notice ?? SESSION_EXPIRED_NOTICE),
    });
  } else if (state.status === 'loading' && notice) {
    endedNotice = notice;
  }
});

// A refreshed session carries the account as it is now - renamed elsewhere,
// say - so the signed-in user stays current.
onSessionRefreshed((session) => {
  if (useAuthStore.getState().status === 'authenticated') {
    useAuthStore.setState({ user: session.user });
  }
});
